import type { Theme } from '@mui/material';

import type { CompetitorPlant } from '@core/types/api/CompetitorPlant/CompetitorPlant';
import type { Plant } from '@core/types/api/Plant/Plant';
import type { ProjectMapPin } from '@core/types/api/ProjectMapPin/ProjectMapPin';
import { parseLatLng } from '@core/utils/addressHelpers';
import { hasValue } from '@core/utils/equality';
import { formatAddressToString } from '@core/utils/strings';

type LocationPinBase = {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  color: string;
};

export type LocationPin =
  | (LocationPinBase & { type: 'plant'; plant: Plant })
  | (LocationPinBase & { type: 'competitor-plant'; competitorPlant: CompetitorPlant })
  | (LocationPinBase & { type: 'job-site'; projectMapPin: ProjectMapPin });

export type PinType = LocationPin['type'];

export type PinVisibility = Record<PinType, boolean>;

export const PIN_TYPE_CONFIG: Record<
  PinType,
  { label: string; getColor: (theme: Theme) => string }
> = {
  plant: {
    label: 'Our locations',
    getColor: (theme) => theme.palette.SlabGreen[600],
  },
  'competitor-plant': {
    label: 'Competitor locations',
    getColor: (theme) => theme.palette.SlabRed[600],
  },
  'job-site': {
    label: 'Job sites for open quotes',
    getColor: (theme) => theme.palette.SlabTurquoise[500],
  },
};

export const buildPlantPins = (plants: Plant[], theme: Theme): LocationPin[] =>
  plants.flatMap((plant) => {
    const coords = parseLatLng(plant.address.latitude, plant.address.longitude);
    if (!hasValue(coords)) return [];

    return [
      {
        id: plant.id,
        name: plant.name,
        address: formatAddressToString(plant.address),
        lat: coords.lat,
        lng: coords.lng,
        type: 'plant',
        plant,
        color: PIN_TYPE_CONFIG.plant.getColor(theme),
      },
    ];
  });

export const buildCompetitorPlantPins = (
  competitorPlants: CompetitorPlant[],
  theme: Theme,
): LocationPin[] =>
  competitorPlants.flatMap((competitorPlant) => {
    const coords = parseLatLng(competitorPlant.address.latitude, competitorPlant.address.longitude);
    if (!hasValue(coords)) return [];

    return [
      {
        id: competitorPlant.id,
        name: competitorPlant.name,
        address: formatAddressToString(competitorPlant.address),
        lat: coords.lat,
        lng: coords.lng,
        type: 'competitor-plant',
        competitorPlant,
        color: PIN_TYPE_CONFIG['competitor-plant'].getColor(theme),
      },
    ];
  });

export const buildJobSitePins = (projectMapPins: ProjectMapPin[], theme: Theme): LocationPin[] =>
  projectMapPins.flatMap((projectMapPin) => {
    const coords = parseLatLng(projectMapPin.address.latitude, projectMapPin.address.longitude);
    if (!hasValue(coords)) return [];

    return [
      {
        id: projectMapPin.id,
        name: projectMapPin.name,
        address: formatAddressToString(projectMapPin.address),
        lat: coords.lat,
        lng: coords.lng,
        type: 'job-site',
        projectMapPin,
        color: PIN_TYPE_CONFIG['job-site'].getColor(theme),
      },
    ];
  });
